// SFCC-11364 (ARTICLES-13, deferred half) — pure breadcrumb-trail logic for the
// goal/module `LearningCenterBreadcrumb` block, split out so it's framework-free
// and unit-testable (and to satisfy react-refresh/only-export-components on the
// component file). Sibling of the article breadcrumb's `trail.ts`; mirrors
// hec-composable's `breadcrumb/learning-center/useLearningCenterPage.jsx`
// (per-pageType crumb construction) + `utils.js` (`addStaticCrumbs`).

export type LearningCenterPageType = "goal" | "module" | "default";

export type Crumb = { label: string; href?: string };

/**
 * SFCC-11723 — localized labels formatted by the component (via `react-intl`)
 * and threaded in so this module stays framework-free and unit-testable. `home`
 * and `learningCenter` are the static-crumb labels; `goalLabel` and
 * `levelModuleLabel` are pre-interpolated templated crumb labels (the component
 * fills `{title}` / `{level}` / `{module}` before passing them in).
 */
export interface TrailLabels {
  home: string;
  learningCenter: string;
  /** Pre-formatted "Goal: <title>" label (module pageType). */
  goalLabel: string;
  /** Pre-formatted "Level N: Module M" label (module pageType). */
  levelModuleLabel: string;
}

/**
 * A Builder `page` reference, inlined under `includeRefs` (the goal/module route
 * forces `includeRefs=true`, so `reference.value.data` is present). We only read
 * the link URL off it; everything else is ignored.
 */
export interface BuilderPageRef {
  value?: { data?: { url?: string } | null } | null;
}

/** `goal.value.data` shape (route inlines the `data.goal` reference). */
export interface GoalData {
  title?: string;
  /** `page` reference → the goal's dashboard URL. */
  dashboard?: BuilderPageRef | null;
}

/** `module.value.data` shape (route inlines the `data.module` reference). */
export interface ModuleData {
  /** Entry id, e.g. `retirement--lvl-2--mod-3`; level/module/goal-slug parsed from it. */
  id?: string;
  /** `page` reference → the module's own page URL. */
  page?: BuilderPageRef | null;
}

/** Goal dashboard URL derived from a module's goal slug (`/learning-center/<slug>`). */
function goalHrefFromSlug(slug: string | null): string | undefined {
  return slug ? `/learning-center/${slug}` : undefined;
}

/**
 * Parse the goal slug from a module entry id. The id is `<goalSlug>--lvl-N--mod-M`
 * (e.g. `retirement--lvl-2--mod-1`), so the slug is the leading `--`-delimited
 * segment. Returns null when the id doesn't carry a slug prefix.
 */
export function parseGoalSlug(id: string | undefined | null): string | null {
  if (!id) return null;
  const head = id.split("--")[0];
  return head && head !== id ? head : null;
}

/** Read a link href off an inlined Builder `page` reference, if present. */
function refHref(ref: BuilderPageRef | null | undefined): string | undefined {
  const url = ref?.value?.data?.url;
  return typeof url === "string" && url.length > 0 ? url : undefined;
}

/**
 * Parse `Level N` / `Module M` from a module entry id. Mirrors the composable:
 * level from `/lvl-(\d+)/`, module number from the id's last character.
 * Returns nulls when the pattern is absent so callers can guard.
 */
export function parseLevelModule(id: string | undefined | null): {
  level: string | null;
  module: string | null;
} {
  if (!id) return { level: null, module: null };
  const match = id.match(/lvl-(\d+)/);
  const level = match ? match[1] : null;
  const last = id[id.length - 1];
  // Only treat a trailing digit as a module number (composable reads the last
  // char unconditionally; guarding here avoids a non-numeric tail leaking in).
  const module = last && /\d/.test(last) ? last : null;
  return { level, module };
}

/**
 * Build the goal/module breadcrumb trail. Mirrors `useLearningCenterPage`'s
 * `switch (pageType)`:
 *   - goal    → `Home › HSA Learning Center › <goal title>` (current, non-clickable)
 *   - module  → `Home › HSA Learning Center › Goal: <title> › Level N: Module M`
 *               (goal crumb clickable → dashboard; module crumb current)
 *   - default → `Home › HSA Learning Center › <page title>` (current)
 * Returns `[]` when the required data hasn't resolved (so the block renders
 * nothing rather than a broken trail).
 *
 * SFCC-11723 — static + templated crumb labels come in via `labels` (localized
 * by the component) instead of being hardcoded here.
 */
export function buildLearningCenterTrail({
  pageType,
  goal,
  module,
  title,
  labels,
}: {
  pageType: LearningCenterPageType | undefined;
  goal?: GoalData | null;
  module?: ModuleData | null;
  /** `data.title` — used by the `default` pageType. */
  title?: string | null;
  /** Localized crumb labels (see `TrailLabels`). */
  labels: TrailLabels;
}): Crumb[] {
  const withStatic = (tail: Crumb[]): Crumb[] => [
    { label: labels.home, href: "/" },
    { label: labels.learningCenter, href: "/learning-center" },
    ...tail,
  ];

  switch (pageType) {
    case "goal": {
      if (!goal?.title) return [];
      // Composable marks the goal crumb non-clickable on the goal page itself.
      return withStatic([{ label: goal.title }]);
    }

    case "module": {
      if (!module) return [];
      const { level, module: moduleNumber } = parseLevelModule(module.id);
      // Level/module are the load-bearing parts of the module crumb; bail if the
      // id doesn't carry them (renders nothing rather than a broken trail).
      if (level == null || moduleNumber == null) return [];

      const tail: Crumb[] = [];

      // Goal crumb (clickable → goal dashboard). The goal *title* lives on the
      // `learning-center-goal` entry, which module pages do NOT inline — the
      // composable reverse-fetches it (level → goal), and the Workers app has no
      // client-side Builder-fetch convention yet (the deferral that split this
      // ticket out of SFCC-11240). So we emit the goal crumb only when a title is
      // resolvable from inlined data; otherwise we render the trail through the
      // module crumb (Home › HSA Learning Center › Level N: Module M) — a
      // verifiable, non-broken subset. The goal `href` always resolves from the
      // module id's goal slug, so the crumb links correctly once a title arrives.
      const goalHref =
        refHref(goal?.dashboard) ?? goalHrefFromSlug(parseGoalSlug(module.id));
      if (goal?.title) {
        tail.push({ label: labels.goalLabel, href: goalHref });
      }

      // Module crumb is the current page → non-clickable (no href).
      tail.push({ label: labels.levelModuleLabel });

      return withStatic(tail);
    }

    case "default": {
      if (!title) return [];
      return withStatic([{ label: title }]);
    }

    default:
      return [];
  }
}
